/** El estado que comparten todas las pantallas: quién soy, en qué portal, y los avisos.
 *
 * `capacidades` viene del servidor y es **la misma lista** que el servidor comprueba. La
 * interfaz esconde lo que no está en ella, así que no puede quedar un botón que lleve a un 403.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

import { api, portal } from './api'
import type { Departamento, QuienSoy, Servidor } from './tipos'

interface Aviso {
  id: number
  texto: string
  tipo: 'bien' | 'malo' | 'normal'
}

interface Valor {
  servidor: Servidor
  yo: QuienSoy
  departamentos: Departamento[]
  recargarDepartamentos: () => Promise<void>
  puedo: (capacidad: string) => boolean
  avisar: (texto: string, tipo?: Aviso['tipo']) => void
  ruta: string
}

const Contexto = createContext<Valor | null>(null)

export function usePortal(): Valor {
  const valor = useContext(Contexto)
  if (!valor) throw new Error('usePortal fuera del proveedor')
  return valor
}

export function ProveedorDelPortal({
  servidor,
  yo,
  avisar,
  children,
}: {
  servidor: Servidor
  yo: QuienSoy
  avisar: (texto: string, tipo?: Aviso['tipo']) => void
  children: ReactNode
}) {
  const ruta = portal(servidor.id)
  const [departamentos, setDepartamentos] = useState<Departamento[]>([])

  const recargarDepartamentos = useCallback(async () => {
    try {
      setDepartamentos(await api.get<Departamento[]>(`${ruta}/departamentos`))
    } catch {
      // Un fallo aquí no debe tumbar la pantalla: los departamentos son decorado en casi
      // todas, y quien los necesite de verdad verá su propio error.
      setDepartamentos([])
    }
  }, [ruta])

  useEffect(() => {
    void recargarDepartamentos()
  }, [recargarDepartamentos])

  const valor = useMemo<Valor>(() => {
    const permisos = new Set(yo.capacidades)
    return {
      servidor,
      yo,
      departamentos,
      recargarDepartamentos,
      puedo: (capacidad: string) => permisos.has(capacidad),
      avisar,
      ruta,
    }
  }, [servidor, yo, departamentos, recargarDepartamentos, avisar, ruta])

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>
}

export type { Aviso }
